import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Button } from '@/components/primitives/Button';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuthStore } from '@/modules/auth';

import { AUTH_TITLE_CLASS, AuthLayout } from '../components/AuthLayout';
import { djangoLoginUrl } from '../services/loginRedirect';

import type { Location } from 'react-router-dom';

export function LoginPage() {
  const { loading, isAuthenticated } = useAuthStore();
  const { t } = useTranslation();
  const location = useLocation();
  const isProd = import.meta.env.PROD;
  const from = (location.state as { from?: Location } | null)?.from;

  // Production sign-in lives in Django, which sends the user back to the map.
  useEffect(() => {
    if (isProd && !loading && !isAuthenticated) {
      window.location.replace(djangoLoginUrl());
    }
  }, [isProd, loading, isAuthenticated]);

  // Show loading during auth check
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">{t('Loading...')}</div>
      </div>
    );
  }

  // Already signed in, e.g. arriving back from the sign-in page
  if (isAuthenticated) {
    return <Navigate to={from ? `${from.pathname}${from.search}` : '/map'} replace />;
  }

  // Development mode: Show login instruction
  if (!isProd) {
    return (
      <AuthLayout>
        <div className="flex flex-col gap-xxl xl:w-[400px]">
          <div className="flex flex-col gap-sm">
            <h1 className={AUTH_TITLE_CLASS}>{t('Login Required')}</h1>
            <p className="text-size-n1 leading-[22px] text-foreground">
              {t('Please log in to continue using NabatOS')}
            </p>
          </div>
          <Button asChild appearance="solid" kind="inverse" scale="m" width="full">
            <a href="http://localhost:8000/admin" target="_blank" rel="noopener noreferrer">
              {t('Login via Admin Panel')}
            </a>
          </Button>
        </div>
      </AuthLayout>
    );
  }

  // Production mode: Show loading while redirecting
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-lg">{t('Redirecting to login...')}</div>
    </div>
  );
}
